/*
 * How it works, and what it will not do.
 *
 * Written for the person who set it up in the morning and wants to know what
 * happens while they are not watching, and for the person who comes back at
 * four o'clock and finds nothing happened at all. The second half — the
 * limits, and the honest statement of what this cannot do — matters more than
 * the first.
 */

import { Link } from "react-router-dom";

import { KIND_BLURB, KIND_LABEL, KIND_RISK } from "../lib/format";
import type { StrategyKind } from "../api/types";

export function HelpPage() {
  return (
    <>
      <div className="page-head">
        <div>
          <h1>How it works</h1>
          <p>What happens while nobody is watching, and what it deliberately will not do.</p>
        </div>
      </div>

      <div className="grid cols-2">
        <div className="card">
          <div className="card-head"><h2>A trading day</h2></div>
          <div className="card-body">
            <ol className="col" style={{ gap: 12, paddingLeft: 18, margin: 0 }}>
              <Step title="Sign in to Zerodha — the one manual step">
                Zerodha ends every API session overnight and SEBI does not allow a
                broker to reopen one without a person. So each trading morning you
                open <Link to="/settings">Settings</Link> and sign in. There is no way
                around this; it is a rule about brokers, not a limitation of this
                application.
              </Step>
              <Step title="Arm the account">
                An account starts halted, every time. Arming it is a separate,
                deliberate act — a connection is not permission to trade.
              </Step>
              <Step title="Walk away">
                From here the engine wakes every thirty seconds, pulls fresh candles
                for everything being watched, and asks each enabled rule what it
                thinks. Nothing else is needed from you.
              </Step>
              <Step title="It stops itself before you would have to">
                A strategy that reaches its daily loss limit or its trade count halts
                and waits. A limit is enforced in Postgres as well as in the code, so
                two things happening at once cannot slip past it.
              </Step>
              <Step title="Come back whenever">
                <Link to="/signals">Decisions</Link> shows everything each rule
                decided and, where it did not act, exactly why.
              </Step>
            </ol>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h2>Paper first</h2></div>
          <div className="card-body col">
            <p className="small muted" style={{ marginTop: 0 }}>
              A new strategy is created on paper and switched off. On paper it does
              everything it would do for real — signals, orders, positions, stops,
              a complete history — and sends nothing to Zerodha.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              Moving it to live is a separate action that names the amount at risk in
              rupees and asks you to type the strategy's name. That friction is
              deliberate. Everything else here is reversible; that step is the one
              that is not.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              Run a rule on paper for a week before it sees real money. A backtest
              tells you what a rule did on candles that have already closed, which is
              the easy half — paper trading tells you what it does in a market that
              has not made up its mind yet.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              <strong>Paper is unrestricted, on purpose.</strong> It draws on its own
              simulated purse and ignores the trade counts, loss limits and position
              caps entirely. A rule cut off after its third signal has been
              interrupted rather than observed, and there is no money to protect. It
              also means a paper position can never hold up a real one — the two
              purses are separate.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              The corollary matters: if the simulated purse is far larger than your
              real capital, the paper P&amp;L is the <em>shape</em> of the rule and
              not what your account would have made. Every limit returns the moment
              the strategy is moved to live.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>The rules on offer</h2>
            <span className="sub">gentlest first</span>
          </div>
          <div className="card-body col" style={{ gap: 14 }}>
            {(Object.keys(KIND_LABEL) as StrategyKind[]).map((k) => (
              <div key={k}>
                <div className="row tight" style={{ marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{KIND_LABEL[k]}</span>
                  <span className={`pill ${KIND_RISK[k].tone === "muted" ? "" : KIND_RISK[k].tone === "warn" ? "warn" : "danger"}`}>
                    {KIND_RISK[k].label}
                  </span>
                </div>
                <div className="small faint">{KIND_BLURB[k]}</div>
                <div className={`small ${KIND_RISK[k].tone}`} style={{ marginTop: 3 }}>
                  {KIND_RISK[k].note}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h2>Which one, and why costs decide it</h2></div>
          <div className="card-body col">
            <p className="small muted" style={{ marginTop: 0 }}>
              There is no rule here that reliably makes the most money, and anybody
              who tells you otherwise is selling something. What there is, is a
              rule that survives a small account better than the others — and on
              a small account that is the same question.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              <strong>The arithmetic that settles it.</strong> Zerodha takes about
              0.22% of a delivery round trip, plus a flat ~₹16 on every sell. On a
              ₹5,000 position that is roughly ₹27 — about 0.55% — which the rule
              has to beat before it has made anything at all. Three trades a day
              is ₹80; twenty is ₹540, which on ₹10,000 of capital is over 5% of
              the account, every day, in charges alone.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              So the rules that trade least win, not because their signals are
              better but because they are charged less often. That is why a new
              strategy defaults to <strong>Supertrend on daily candles</strong>:
              it is the combination that decides fewest times. The flat
              depository charge also does not shrink with the trade, so a few
              larger positions pay it far less often than many small ones.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              <strong>Backtests overstate all of this.</strong> They fill at the
              closing price, never miss a bar, and — because you chose the rule
              after seeing which one looked best — describe a strategy fitted to
              the past. A rule that looks good on a backtest and mediocre on
              paper is a rule that is mediocre.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h2>What stops it</h2></div>
          <div className="card-body col" style={{ gap: 10 }}>
            <Limit k="Per trade" d="How much one position may cost. The quantity is worked out from this and the price." />
            <Limit k="Positions at once" d="Per strategy. Combined with your total capital, which is checked across every strategy before anything new is opened." />
            <Limit k="Trades a day" d="Counted per strategy, per calendar day, in a row the database locks — so two ticks at once cannot both slip past it." />
            <Limit k="Daily loss" d="Realised losses. Reaching it halts that strategy for the day and it waits for you to clear it." />
            <Limit k="Account daily loss" d="The same idea across every strategy together." />
            <Limit k="Account trades a day" d="Entries across every strategy together, set in Settings. This is the limit that bites first — costs are charged per trade, so a busy morning is expensive before it is ever unprofitable." />
            <Limit k="…and none of them on paper" d="Every count and loss limit above applies to live strategies only. A paper strategy is bounded by its own simulated purse and nothing else, so a run shows you the whole rule rather than its first three signals." />
            <Limit k="Stop, target, trailing stop" d="Checked on every tick against the live price, not only when a candle closes." />
            <Limit k="Entry window" d="Nothing new opens outside it — not in the first minutes of chaos, not in the last." />
            <Limit k="Server ceilings" d="A largest single order and a largest daily loss, set on the server. No strategy can raise them." />
            <Limit k="Halt everything" d="At the top of every page. Stops anything new immediately; closing what is open is a second, separate choice." />
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h2>What it will not do</h2></div>
          <div className="card-body col" style={{ gap: 10 }}>
            <Limit k="It will not make money" d="No trading rule is a way of making money reliably. Every figure here is gross of brokerage, taxes and slippage, and those alone turn many marginally profitable strategies into losing ones." />
            <Limit k="It will not trade what it cannot price" d="No Zerodha session means no candles, no live prices and no orders — it does nothing rather than guessing." />
            <Limit k="It will not restart itself into trading" d="After a deploy, a reboot or a restored backup, the engine comes up idle and every account halted." />
            <Limit k="It will not resume after a limit trips" d="A halted strategy waits for a person. That is the whole point of a limit." />
            <Limit k="It will not know about a holiday" d="There is no hard-coded holiday list to go stale. On a holiday Zerodha returns no fresh candles and the rules do nothing, which is the right answer reached without a list anybody has to maintain." />
            <Limit k="It will not let one person see another's account" d="Strategies, orders and positions are yours alone. Admins are not exempted." />
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h2>Where the numbers come from</h2></div>
          <div className="card-body col">
            <p className="small muted" style={{ marginTop: 0 }}>
              Live prices arrive on Zerodha's own tick feed — a WebSocket, several
              updates a second. If it drops, or a session expires, every price on the
              screen is greyed out and the strip at the top says how old the last one
              is. A price that has stopped updating is never shown as though it were
              current.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              Decisions are made on candles that have <em>closed</em>, not on the
              live price. An indicator computed from a bar still forming changes as
              the bar changes, which is how a backtest ends up describing a strategy
              nobody could have traded.
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              Money is held to four decimal places, as a decimal and never a floating
              point number, everywhere from the signal to the order.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function Step({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <li>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
      <div className="small faint">{children}</div>
    </li>
  );
}

function Limit({ k, d }: { k: string; d: string }) {
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{k}</div>
      <div className="small faint">{d}</div>
    </div>
  );
}
